// Captures React hydration / minified React errors with cause-distinguishing
// context so they can be triaged in Rollbar.
// See docs/ssr/SKILL.md ("Debugging minified React errors").
//
// Why this exists: React 18 hydration mismatches (#418/#421/#422/#423/#425) are
// *recoverable* errors. They never reach our <ErrorBoundary> (componentDidCatch),
// and react-on-rails 16.6.0 calls `hydrateRoot` with no `onRecoverableError`
// option (see node_modules/react-on-rails/lib/reactApis.cjs), so there is no
// in-React hook for them. React routes recoverable errors to `reportError()`,
// which dispatches a global window 'error' event — which is what we listen for.
//
// In production React's error messages are minified to "Minified React error
// #NNN", and the server-vs-client diff is compiled out entirely. We cannot
// recover *what* mismatched here; instead we record *where / when / who* (route,
// user state, whether the page was translated by an extension) so the dominant
// cause can be identified and then reproduced in a dev build.

const INSTALL_FLAG = '__hydrationReporterInstalled';

// Production message, e.g. "Minified React error #425; visit https://reactjs.org/..."
const MINIFIED_REACT_ERROR = /Minified React error #(\d+)/;

// Un-minified messages emitted by the development React build.
const DEV_HYDRATION_ERROR = /hydrat|did not match|text content does not match/i;

// Recoverable hydration error codes worth bucketing as `react-hydration`.
const HYDRATION_CODES = new Set(['130', '418', '421', '422', '423', '425']);

interface Classification {
  reactErrorCode: string | null;
  isHydration: boolean;
}

function classifyMessage(message: string | undefined): Classification | null {
  if (!message) return null;

  const minified = message.match(MINIFIED_REACT_ERROR);
  if (minified) {
    const code = minified[1];
    return { reactErrorCode: code, isHydration: HYDRATION_CODES.has(code) };
  }

  if (DEV_HYDRATION_ERROR.test(message)) {
    return { reactErrorCode: null, isHydration: true };
  }

  return null;
}

// Google Translate (and similar extensions) add these classes to <html> and
// rewrite text nodes between SSR and hydration — a very common production-only
// cause of #425 that never reproduces locally.
function isPageTranslated(): boolean {
  const el = document.documentElement;
  return el.classList.contains('translated-ltr') || el.classList.contains('translated-rtl');
}

function userState() {
  let loggedIn = false;
  let subscriptionType: string | undefined;

  try {
    // `currentUser` is a global set by app/views/layouts/shared/_current_user.html.erb.
    // It may be absent on pages that don't render that partial, hence the guard.
    if (typeof currentUser !== 'undefined' && currentUser) {
      const cu = currentUser as unknown as { type?: string };
      subscriptionType = cu.type;
      loggedIn = !!cu.type && cu.type !== 'logged-out';
    }
  } catch {
    // No usable currentUser global on this page.
  }

  return {
    loggedIn,
    isSubscriber: window.isCurrentUserSubscriber,
    subscriptionType,
  };
}

function handleError(event: ErrorEvent): void {
  const message = event.error?.message ?? event.message;
  const info = classifyMessage(message);
  if (!info) return;

  const { loggedIn, isSubscriber, subscriptionType } = userState();

  const extra = {
    source: 'reportHydrationErrors',
    // Lets us split true hydration mismatches from other minified React throws.
    type: info.isHydration ? 'react-hydration' : 'react-minified',
    reactErrorCode: info.reactErrorCode,
    path: window.location.pathname,
    url: window.location.href,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    // Suspect A — DOM-mutating extension / translation.
    isTranslated: isPageTranslated(),
    htmlLang: document.documentElement.lang || null,
    htmlClass: document.documentElement.className || null,
    // Suspect B — server/client auth or subscription divergence.
    loggedIn,
    isSubscriber,
    subscriptionType,
  };

  if (window.Rollbar) {
    window.Rollbar.error(event.error ?? new Error(message), extra);
  } else if (typeof console !== 'undefined') {
    // Dev has no Rollbar; the React dev build already prints the full diff.
    // eslint-disable-next-line no-console
    console.debug('[reportHydrationErrors]', extra);
  }
}

// Pull the error message out of a Rollbar payload (shape varies by item type).
function payloadMessage(payload: unknown): string {
  const p = payload as {
    body?: {
      trace?: { exception?: { message?: string } };
      trace_chain?: Array<{ exception?: { message?: string } }>;
      message?: { body?: string };
    };
  };
  return (
    p?.body?.trace?.exception?.message ??
    p?.body?.trace_chain?.[0]?.exception?.message ??
    p?.body?.message?.body ??
    ''
  );
}

// Rollbar's `captureUncaught` already reports these window 'error' events. We
// re-report them manually above with richer context, so tell Rollbar to drop the
// bare auto-captured (uncaught) copy and keep only our enriched one.
function installDedupe(): void {
  if (!window.Rollbar?.configure) return;

  try {
    window.Rollbar.configure({
      checkIgnore: (isUncaught: boolean, _args: unknown, payload: unknown) => {
        if (!isUncaught) return false;
        return classifyMessage(payloadMessage(payload)) != null;
      },
    });
  } catch {
    // configure is best-effort; worst case we get a duplicate Rollbar item.
  }
}

function install(): void {
  if (typeof window === 'undefined') return;

  const w = window as unknown as Record<string, boolean>;
  if (w[INSTALL_FLAG]) return;
  w[INSTALL_FLAG] = true;

  installDedupe();
  window.addEventListener('error', handleError);
}

install();

export {};
